import { Flex, Text } from "@chakra-ui/react";
import { useField } from "formik";
import { useCallback, useMemo, useState } from "react";
import { FormikCustomSelect } from "src/components/Inputs/CustomSelect";
import { FormikSwitchInput } from "src/components/Inputs/FormikSwitchInput";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import { useGetSchools } from "src/generated/hooks/form-service/queries";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useGlossary } from "src/hooks/useGlossary";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import * as GQL from "src/types/graphql";
import { useFormTemplateContext } from "../formTemplateContext";
import { GET_FORM_TEMPLATE_FORMS_COUNT } from "../graphql/queries";
import { audienceSchoolDropdownLabel, type AudienceType } from "./schemas";

type AudienceOption = {
  key: AudienceType;
  label: string;
  description: string;
};

const AUDIENCE_OPTIONS: AudienceOption[] = [
  {
    key: "everyone",
    label: "Everyone",
    description: "Anyone who hasn't already created a response can create one",
  },
  {
    key: "new",
    label: "New students",
    description:
      "Students not attending any school from the organization in this enrollment period",
  },
  {
    key: "current",
    label: "Current students",
    description:
      "Students currently attending a school from the organization in this enrollment period",
  },
];

const ANY_SCHOOL_ID = "__any_school__";

type SchoolOption = { id: string; label: string; description?: string };

const AUDIENCE_CHANGE_CONFIRM_BODY = (
  <Text>
    There may be already form responses associated with this form, that are not
    part of the new audience.{" "}
    <Text as="span" fontWeight="bold">
      Changing this will not impact those responses in any way. Changing the
      form audience will only impact new form responses that can be started.
    </Text>
  </Text>
);

export const AcceptNewFormEntriesSection: React.FC = () => {
  const [{ value: lockedValue }] = useField<boolean>("locked");
  const [{ value: audienceType }, , audienceTypeHelper] =
    useField<AudienceType>("audienceType");
  const [
    { value: audienceSchoolIds },
    _audienceSchoolIdsMeta,
    audienceSchoolIdsHelper,
  ] = useField<string[]>("audienceSchoolIds");
  const { formTemplate } = useFormTemplateContext();
  const { glossary, glossaryTransform } = useGlossary({
    enrollmentPeriodId: formTemplate.enrollment_period.id,
  });

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "Change form audience?",
    body: AUDIENCE_CHANGE_CONFIRM_BODY,
    cancelButton: { label: "Cancel" },
    confirmButton: { label: "Continue" },
  });

  const { remoteData: formsCountRD } = useRemoteDataQuery<
    GQL.GetFormTemplateFormsCount,
    GQL.GetFormTemplateFormsCountVariables
  >(GET_FORM_TEMPLATE_FORMS_COUNT, {
    variables: { form_template_id: formTemplate.id },
    fetchPolicy: "network-only",
  });

  const hasFormResponses =
    (formsCountRD.toNullable()?.form_template_by_pk?.forms_aggregate?.aggregate
      ?.count ?? 0) > 0;

  const [schoolSelectKey, setSchoolSelectKey] = useState(0);
  const [hasShownAudienceWarning, setHasShownAudienceWarning] = useState(false);

  const shouldShowAudienceModal = hasFormResponses && !hasShownAudienceWarning;

  const applyAudienceTypeChange = useCallback(
    async (newType: AudienceType | null) => {
      if (shouldShowAudienceModal) {
        if (!(await confirm())) {
          return;
        }
        setHasShownAudienceWarning(true);
      }

      const key = newType ?? "everyone";
      await audienceTypeHelper.setValue(key);
      if (key !== "current") {
        await audienceSchoolIdsHelper.setValue([]);
      }
      audienceTypeHelper.setTouched(true);
    },
    [
      shouldShowAudienceModal,
      confirm,
      audienceTypeHelper,
      audienceSchoolIdsHelper,
    ]
  );

  const applyAudienceSchoolIdsChange = useCallback(
    async (schoolIds: string[]) => {
      if (shouldShowAudienceModal) {
        if (!(await confirm())) {
          setSchoolSelectKey((k) => k + 1);
          return;
        }
        setHasShownAudienceWarning(true);
      }
      await audienceSchoolIdsHelper.setValue(schoolIds);
    },
    [shouldShowAudienceModal, confirm, audienceSchoolIdsHelper]
  );

  const organizationId = formTemplate.enrollment_period.organization_id;

  const audienceOptionsWithGlossary = useMemo(
    () =>
      AUDIENCE_OPTIONS.map((opt) => ({
        ...opt,
        label: glossaryTransform(opt.label),
        description: glossaryTransform(opt.description),
      })),
    [glossaryTransform]
  );

  const { data: schoolsResponse } = useGetSchools(
    {
      query: {
        organizationId: organizationId!,
      },
    },
    [organizationId],
    {
      enabled: !!organizationId && !lockedValue && audienceType === "current",
    }
  );

  const schools = useMemo(
    () => schoolsResponse?.data ?? [],
    [schoolsResponse?.data]
  );

  const anySchoolOption: SchoolOption = useMemo(
    () => ({
      id: ANY_SCHOOL_ID,
      label: glossaryTransform("Any School"),
      description: glossaryTransform(
        `Students attending any school within the ${formTemplate.enrollment_period.name} EP`
      ),
    }),
    [glossaryTransform, formTemplate.enrollment_period.name]
  );

  const schoolOptions = useMemo(() => {
    const orgSchoolOptions = schools.map((s) => ({
      id: s.id,
      label: audienceSchoolDropdownLabel(s.name, s.status),
    }));
    const removedSchoolIds = audienceSchoolIds.filter(
      (id) => id !== ANY_SCHOOL_ID && !schools.some((s) => s.id === id)
    );
    const removedSchoolOptions: SchoolOption[] = removedSchoolIds.map((id) => ({
      id,
      label: glossaryTransform("School (inactive or removed)"),
    }));
    return [anySchoolOption, ...orgSchoolOptions, ...removedSchoolOptions];
  }, [anySchoolOption, schools, audienceSchoolIds, glossaryTransform]);

  const selectedSchoolValues = useMemo(
    (): SchoolOption[] =>
      audienceSchoolIds.length === 0
        ? [anySchoolOption]
        : audienceSchoolIds.map((schoolId) => {
            const school = schools.find((s) => s.id === schoolId);
            return school
              ? {
                  id: school.id,
                  label: audienceSchoolDropdownLabel(
                    school.name,
                    school.status
                  ),
                }
              : {
                  id: schoolId,
                  label: glossaryTransform("School (inactive or removed)"),
                };
          }),
    [audienceSchoolIds, schools, anySchoolOption, glossaryTransform]
  );

  const acceptingEntries = !lockedValue;

  return (
    <>
      <Flex>
        <FormikSwitchInput<boolean>
          label=""
          name="locked"
          checked={[false, ""]}
          unchecked={[true, ""]}
          formControlProps={{ width: "fit-content" }}
        />

        <Flex direction="column" width="100%">
          <Text>Accept new form entries</Text>
          <Text fontSize="sm">
            When enabled, families will be able to start a new entry for this
            form - unless they already have one in progress.
          </Text>

          {acceptingEntries && (
            <Flex mt={2} gap={2} direction="column">
              <FormikCustomSelect<AudienceOption>
                name="audienceType"
                label="Select audience"
                items={audienceOptionsWithGlossary}
                onChange={(item) => applyAudienceTypeChange(item?.key ?? null)}
                renderItem={(item) => (
                  <Flex direction="column">
                    <Text color="gray.900">{item.label}</Text>
                    <Text fontSize="sm" color="blackAlpha.700">
                      {item.description}
                    </Text>
                  </Flex>
                )}
                renderSelectedItem={(item) => (
                  <Text>{item?.label ?? "Select audience"}</Text>
                )}
              />

              {audienceType === "current" && (
                <MultiSelectInput<SchoolOption>
                  key={schoolSelectKey}
                  size="md"
                  label={glossary`Select schools`}
                  options={schoolOptions}
                  initialValues={selectedSchoolValues}
                  onChange={(selected) => {
                    const hasAnySchool = selected.some(
                      (s) => s.id === ANY_SCHOOL_ID
                    );
                    const schoolIds = selected
                      .filter((s) => s.id !== ANY_SCHOOL_ID)
                      .map((s) => s.id);
                    if (hasAnySchool && schoolIds.length > 0) {
                      if (audienceSchoolIds.length > 0) {
                        applyAudienceSchoolIdsChange([]);
                      } else {
                        applyAudienceSchoolIdsChange(schoolIds);
                      }
                    } else if (hasAnySchool) {
                      applyAudienceSchoolIdsChange([]);
                    } else {
                      applyAudienceSchoolIdsChange(schoolIds);
                    }
                  }}
                  placeholder={glossary`Search schools...`}
                  renderDropdownItem={(item) => (
                    <Flex direction="column">
                      <Text color="gray.900">{item.label}</Text>
                      {item.description && (
                        <Text fontSize="sm" color="blackAlpha.700">
                          {item.description}
                        </Text>
                      )}
                    </Flex>
                  )}
                />
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
      {confirmationDialog}
    </>
  );
};
